import React from "react"
import ProjectList from "../../treegrid/project-list"
import ProjectsViewHeader from "../../projects/components/ProjectsViewHeader"
import CircularProgress from "@material-ui/core/CircularProgress"
import { Theme, makeStyles, useMediaQuery } from "@material-ui/core"
import { useAppContext } from "../../app-context/use-app-context"
import { useRouter } from "../../hooks/use-router"
import { useUserTimeZone } from "../../hooks/use-user-time-zone"
import { ProjectsViewOptions } from "../../projects/components/ProjectsViewSelect"
import { useDateFormat } from "../../users/hooks/use-date-format"
import ErrorList from "../../components/ErrorList"
import TempalateList from "../../treegrid/template-list"
import ProjectCards from "../../projects/components/ProjectCards"
import { useAuthUserMembership } from "../../memberships/hooks/use-auth-user-membership"
import { useOptions } from "../../options/hooks/use-options"
import {
  projectListDefaultColumnOrderByView,
  projectListDefaultVisibleColumnsByView,
} from "./utils/project-list-column-settings"
import { useProjectListRouteData } from "./hooks/use-project-list-route-data"
import { useTranslation } from "react-i18next"

type Translations = typeof defaultTranslations

const ProjectsRoute = () => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))
  const translations = useTranslations()
  const { dateFormat, dateSeparator } = useDateFormat()
  const userTimeZone = useUserTimeZone()
  const router = useRouter()
  const {
    appContext: { mainContext, subContext },
  } = useAppContext()
  const context = subContext || mainContext
  const { view = "currentAndFuture" } = (router.query || {}) as { view?: ProjectsViewOptions }
  const isTemplatesView = view === "templates"
  const showTemplateList = !isSmallScreen && isTemplatesView
  const showProjectList = !isSmallScreen && !isTemplatesView
  const showProjectCards = isSmallScreen
  const {
    calendarSettingsById,
    calendarSettingsQueryErrors,
    firstDayOfWeek,
    holidays,
    holidaysQueryErrors,
    isError,
    isErrorHolidays,
    isErrorProjectCalendarSettings,
    isLoading,
    isLoadingHolidays,
    isLoadingProjectCalendarSettings,
    projects,
    query,
    settingsKey,
    status,
    weekendDays,
  } = useProjectListRouteData({ context, mainContext, showProjectList, view })

  const showArchivedTitle = view === "archived"
  const classes = useStyles()
  const { options } = useOptions()
  const [key, setKey] = React.useState(JSON.stringify(query))
  const authUserMembership = useAuthUserMembership()
  const canCreateProjects = Boolean(mainContext?.type === "user" || authUserMembership?.canCreateProjects)

  React.useEffect(() => {
    if (status === "fulfilled") {
      // Need to recreate the treegrid when the query is changed and the data is completed refetching (status = fulfilled)
      setKey(`${JSON.stringify(query)}__${settingsKey}`)
    }
  }, [status, query, settingsKey])

  if (isLoading || isLoadingProjectCalendarSettings || isLoadingHolidays) return <CircularProgress />
  if (!context || !mainContext) return <ErrorList errors={[translations.contextNotFoundError]} />
  if (isError) return <ErrorList errors={[translations.fetchErrorProjects]} />
  if (isErrorProjectCalendarSettings) return <ErrorList errors={calendarSettingsQueryErrors} />
  if (isErrorHolidays) return <ErrorList errors={holidaysQueryErrors} />

  return (
    <div className={classes.viewContainer}>
      <ProjectsViewHeader
        canCreateProjects={canCreateProjects}
        titlePrefix={context.name}
        showArchivedTitle={showArchivedTitle}
      />
      {showProjectCards && <ProjectCards projects={projects} />}
      {showTemplateList && (
        <TempalateList
          projects={projects}
          canCreateProjects={canCreateProjects}
          timeZone={userTimeZone}
          dateFormat={dateFormat}
        />
      )}
      {showProjectList && (
        <ProjectList
          key={key}
          projects={projects}
          canCreateProjects={canCreateProjects}
          dateFormat={dateFormat}
          timeZone={userTimeZone}
          options={options}
          gridInfo={`${context.name} - Projects`}
          dateSeparator={dateSeparator}
          firstDayOfWeek={firstDayOfWeek}
          defaultColumnOrder={projectListDefaultColumnOrderByView[view]}
          defaultVisibleColumns={projectListDefaultVisibleColumnsByView[view]}
          weekendDays={weekendDays}
          holidays={holidays}
          calendarSettingsById={calendarSettingsById}
        />
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  viewContainer: {
    marginTop: theme.spacing(0.75),
    "& > :first-child": {
      marginBottom: theme.spacing(1.125),
    },
  },
}))

const defaultTranslations = {
  contextNotFoundError: "Context not found",
  fetchErrorProjects: "Failed to fetch projects",
  projectsEmpty: "You don't have any projects here yet",
  addProject: "Add project",
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { t } = useTranslation("translation")

  return {
    contextNotFoundError: t("contextNotFoundError", defaults.contextNotFoundError),
    fetchErrorProjects: t("fetchErrorProjects", defaults.fetchErrorProjects),
    projectsEmpty: t("projectsEmpty", defaults.projectsEmpty),
    addProject: t("projectsViewHeader.addProjectButtonLabel") || defaults.addProject,
  }
}

export default ProjectsRoute
